// Models
import { IExerciseData } from 'storage/exercise/models'
import {
  IFormInputs,
  IModelInput,
  IWorkoutSetInput,
  TFormValues,
  TSetSpecialSet,
  TSpecialSetInput,
} from 'models'
import { TMenuDropdownOption } from 'heeds-ds/src/models'

// React
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

// Libraries
import { FormProvider, UseFormReturn, useForm } from 'react-hook-form'
import { ThemeContext } from 'styled-components'
import { generatePath, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import { yupResolver } from '@hookform/resolvers/yup'

// Misc
import { buttonClickTracking } from 'utils/tracking'
import { getDivisionType, getModelName, uid } from 'utils/functions'
import {
  prepareModelsToRedux,
  prepareSpecialWorkoutSetToState,
} from 'filters/workouts'
import { workoutSpecialSetSchema } from 'schemas'
import { success } from 'storage/workoutModel/duck'
import { urls } from 'routes/paths'
import { useModal } from 'hooks'

// Components
import * as Styled from './styled'
import {
  ModalWorkoutModel,
  Tab,
  WorkoutSets,
  WorkoutSpecialSet,
} from 'components'
import { Icon, IconButton } from 'heeds-ds'

interface ISetToExerciseFormInputs {
  cadence: string
  repetition: string
  weight: string
}

export interface ISpecialSetFormInputs {
  exercises: ISetToExerciseFormInputs[]
  comments: string
  interval: string
  quantity: string
}

type Props = {
  addWorkoutModel: (model: IModelInput) => void
  addWorkoutSet: (modelId: string, set: IWorkoutSetInput) => void
  duplicateWorkoutModel: (index: number) => void
  methods: UseFormReturn<IFormInputs, TFormValues>
  openExerciseGifModal: (exercise: IExerciseData) => void
  removeExerciseFromWorkoutSet: (
    modelId: string,
    workoutSetId: string,
    exerciseIndex: number,
  ) => void
  removeWorkoutModel: (id: string) => void
  removeWorkoutSet: (modelId: string, workoutSetId: string) => void
  replicateWorkoutSetFields: (modelId: string, set: IWorkoutSetInput) => void
  selectedModelIndex: number
  selectWorkoutModel: (index: number) => void
  setSpecialSet: TSetSpecialSet
  specialSet?: TSpecialSetInput
  updateWorkoutSet: (
    modelId: string,
    workoutSetId: string,
    updatedWorkoutSet: IWorkoutSetInput,
  ) => void
  stateModels: IModelInput[]
}

const WorkoutModels: FC<Props> = ({
  addWorkoutModel,
  addWorkoutSet,
  duplicateWorkoutModel,
  methods,
  openExerciseGifModal,
  removeExerciseFromWorkoutSet,
  removeWorkoutModel,
  removeWorkoutSet,
  replicateWorkoutSetFields,
  selectedModelIndex,
  selectWorkoutModel,
  setSpecialSet,
  specialSet,
  updateWorkoutSet,
  stateModels,
}) => {
  const { id = '', routine_id = '' } = useParams()
  const { openModal, isVisible } = useModal()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useContext(ThemeContext)

  const [selectedTab, setSelectedTab] = useState(0)
  const [showIndicator, setShowIndicator] = useState<
    'Left' | 'Right' | undefined
  >(undefined)

  const tabsRef = useRef<HTMLDivElement>(null)

  const specialSetFormMethods = useForm<TSpecialSetInput>({
    resolver: yupResolver(workoutSpecialSetSchema),
    delayError: 800,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  })
  const { handleSubmit, setValue, unregister } = specialSetFormMethods

  const workoutModel = stateModels[selectedModelIndex]

  const getActions = useCallback(
    (model: IModelInput): TMenuDropdownOption[] => {
      const modelIndex = stateModels.findIndex(
        (stateModel) => stateModel.id === model.id,
      )
      return [
        {
          label: 'Duplicar',
          icon: 'contentCopy',
          onClick: () => duplicateWorkoutModel(modelIndex),
        },
        {
          label: 'Excluir',
          icon: 'delete',
          color: theme.colors.text.critical,
          onClick: () => removeWorkoutModel(model.id),
        },
      ]
    },
    [
      duplicateWorkoutModel,
      removeWorkoutModel,
      stateModels,
      theme.colors.text.critical,
    ],
  )

  const getCountSets = (model?: IModelInput): number => {
    if (!model) return 0

    if (Object.values(model.workout_set).length === 0) return 0

    return Object.values(model.workout_set)
      .map((setToExercise) => setToExercise.exercises.length)
      .reduce((accumulator, currentValue) => accumulator + currentValue)
  }

  const countSets = useMemo(() => getCountSets(workoutModel), [workoutModel])

  const handleOpenModal = useCallback(() => {
    openModal('added-workout-models-modal')
  }, [openModal])

  const handleScroll = useCallback(() => {
    if (showIndicator === 'Left') {
      tabsRef.current?.scroll({ left: 0 })
      setShowIndicator('Right')
    } else {
      tabsRef.current?.scroll({ left: tabsRef.current?.scrollWidth })
      setShowIndicator('Left')
    }
  }, [showIndicator])

  const handleAddWorkoutModal = useCallback(() => {
    addWorkoutModel({
      id: 'NEW' + uid(),
      name: getModelName(
        stateModels?.length,
        getDivisionType(stateModels[0].name),
      ),
      workout_set: {},
      workout_routine: workoutModel.workout_routine,
    })
  }, [addWorkoutModel, stateModels, workoutModel.workout_routine])

  const close = useCallback(() => {
    if (specialSet) {
      unregister(['comments', 'exercises', 'interval', 'quantity'])
    }
    setSpecialSet(undefined)
  }, [setSpecialSet, specialSet, unregister])

  const removeSet = useCallback(() => {
    if (stateModels && specialSet) {
      const modelId = stateModels[selectedModelIndex].id
      const setId = specialSet.id
      removeWorkoutSet(modelId, setId)
      setSpecialSet(undefined)
      methods.unregister(
        `models.${selectedModelIndex}.workout_set.${
          Object.values(stateModels[selectedModelIndex].workout_set).length - 1
        }`,
      )
    }
  }, [
    methods,
    removeWorkoutSet,
    selectedModelIndex,
    setSpecialSet,
    specialSet,
    stateModels,
  ])

  const renderTabs = useMemo(
    () =>
      stateModels?.map((stateModel, modelIndex) => {
        const model = stateModels[modelIndex]
        return (
          <Tab
            name={`models.${modelIndex}`}
            key={stateModel.id}
            actions={getActions(model)}
            active={selectedTab === modelIndex}
            disabled={specialSet !== undefined}
            label={stateModel.id}
            onChangeName={(updatedName) => (model.name = updatedName)}
            onClick={() => selectWorkoutModel(modelIndex)}
          />
        )
      }),
    [getActions, selectWorkoutModel, selectedTab, specialSet, stateModels],
  )

  const renderForm = useMemo(
    () =>
      stateModels.map((field, index) => (
        <Styled.ShowList
          key={field.id}
          isVisible={index === selectedModelIndex}
        >
          <WorkoutSets
            formIndex={index}
            onEditSpecialSet={({ id, quantity, interval, exercises }) => {
              setValue('id', id)
              setValue('quantity', quantity)
              setValue('interval', interval)
              setValue('exercises', exercises)
            }}
            replicateWorkoutSetFields={replicateWorkoutSetFields}
            openExerciseGifModal={openExerciseGifModal}
            removeExerciseFromWorkoutSet={removeExerciseFromWorkoutSet}
            removeWorkoutSet={removeWorkoutSet}
            selectedModelIndex={selectedModelIndex}
            setSpecialSet={setSpecialSet}
            specialSet={specialSet}
            stateModels={stateModels}
          />
        </Styled.ShowList>
      )),
    [
      openExerciseGifModal,
      removeExerciseFromWorkoutSet,
      removeWorkoutSet,
      replicateWorkoutSetFields,
      selectedModelIndex,
      setSpecialSet,
      setValue,
      specialSet,
      stateModels,
    ],
  )

  const onSubmit = useCallback(
    (formData: IFormInputs) => {
      const reduxData = prepareModelsToRedux(formData, stateModels)

      dispatch(success({ workoutModels: reduxData }))

      navigate(generatePath(urls.workoutRoutineReview, { id, routine_id }))
    },
    [dispatch, id, navigate, routine_id, stateModels],
  )

  const onSubmitSpecialSet = useCallback(
    (formData: TSpecialSetInput) => {
      if (specialSet) {
        const modelId = stateModels[selectedModelIndex].id
        const updated = prepareSpecialWorkoutSetToState(formData, specialSet)
        specialSet.index !== undefined
          ? updateWorkoutSet(modelId, specialSet.id, updated)
          : addWorkoutSet(modelId, { ...updated, id: 'NEW' + uid() })
      }
      close()
    },
    [
      addWorkoutSet,
      close,
      selectedModelIndex,
      specialSet,
      stateModels,
      updateWorkoutSet,
    ],
  )

  useEffect(() => {
    setSelectedTab(selectedModelIndex)
  }, [selectedModelIndex])

  useEffect(() => {
    if (
      tabsRef.current &&
      tabsRef.current.scrollWidth > tabsRef.current.clientWidth
    ) {
      setShowIndicator('Right')
    } else {
      setShowIndicator(undefined)
    }
  }, [tabsRef.current?.scrollWidth])

  return (
    <Styled.Container>
      <Styled.TabsContainer>
        {stateModels?.length > 0 && (
          <Styled.ScrollTabs ref={tabsRef}>
            <Styled.Tabs>
              <FormProvider {...methods}>{renderTabs}</FormProvider>
            </Styled.Tabs>
          </Styled.ScrollTabs>
        )}
        {showIndicator && (
          <Styled.ScrollButton
            left={showIndicator === 'Left'}
            onClick={handleScroll}
            type="button"
          >
            <Icon iconName={`chevron${showIndicator}`} color="#000" size={32} />
          </Styled.ScrollButton>
        )}

        <IconButton
          disabled={specialSet !== undefined}
          iconName="add"
          onClick={handleAddWorkoutModal}
          size="xsmall"
          track={buttonClickTracking}
          trackName="add_workout_model_state"
          variation="main"
        />
      </Styled.TabsContainer>

      {workoutModel && countSets > 0 && (
        <Styled.ExerciseCount onClick={handleOpenModal}>
          {countSets === 1
            ? '1 exercício adicionado'
            : `${countSets} exercícios adicionados`}
        </Styled.ExerciseCount>
      )}
      {specialSet ? (
        <FormProvider {...specialSetFormMethods}>
          <Styled.Form onSubmit={handleSubmit(onSubmitSpecialSet)}>
            <WorkoutSpecialSet
              onCancel={close}
              onClickImage={openExerciseGifModal}
              onRemove={removeSet}
              openExercisesModal={() => undefined}
              removeExerciseFromWorkoutSet={removeExerciseFromWorkoutSet}
              specialSet={specialSet}
            />
          </Styled.Form>
        </FormProvider>
      ) : (
        <FormProvider {...methods}>
          <Styled.Form id="sets-form" onSubmit={methods.handleSubmit(onSubmit)}>
            {renderForm}
          </Styled.Form>
        </FormProvider>
      )}

      {isVisible === 'added-workout-models-modal' && (
        <ModalWorkoutModel
          selectedModelIndex={selectedModelIndex}
          stateModels={stateModels}
        />
      )}
    </Styled.Container>
  )
}

export default WorkoutModels
